import { FC, Fragment, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { FloatLoadingIndicator } from '@components/FloatLoadingIndicator'
import { PageTitle } from '@components/PageTitle'

import { usePermissions } from '@modules/permissions/core/PermissionsProvider'

import { DefaultLayoutConfig } from '@/constants/defaultLayoutConfig'
import { PermissionEnum } from '@/constants/permission'
import { useLayout } from '@/layouts/core'
import { PageLink } from '@/layouts/models'
import { LayoutSetup } from '@/utils/layoutSetup'

const dashboardBreadCrumbs: Array<PageLink> = [
  {
    title: 'Home',
    path: '/dashboard',
    isSeparator: false,
    isActive: false,
  },
]

const DashboardPage: FC = () => {
  const { setLayout } = useLayout()
  const { hasPermissions } = usePermissions()
  const [containerSize, setContainerSize] = useState<[number, number]>()
  const containerRef = useRef<HTMLDivElement>(null)
  const hasPowerBIPermission = hasPermissions([PermissionEnum.ADMIN_POWERBI_SHOW])

  useEffect(() => {
    if (hasPowerBIPermission) {
      const { header, aside, content, footer } = LayoutSetup.config

      setLayout({
        aside: {
          ...aside,
          float: true,
          minimized: true,
        },
        header: {
          ...header,
          width: 'fluid',
        },
        content: {
          ...content,
          width: 'fluid',
        },
        footer: {
          ...footer,
          width: 'fluid',
        },
      })

      return () => setLayout(DefaultLayoutConfig)
    }
  }, [hasPowerBIPermission, setLayout])

  useEffect(() => {
    if (containerRef.current) {
      setTimeout(() => {
        setContainerSize([containerRef.current!.clientWidth, containerRef.current!.clientHeight])
      }, 1000)
    }
  }, [])

  if (hasPowerBIPermission) {
    return (
      <Fragment>
        {containerSize ? (
          <iframe
            title="Power BI Dashboard"
            src="https://app.powerbi.com/reportEmbed?reportId=be0a1491-b102-4324-aeca-f99e2150d710&autoAuth=true&ctid=5764162d-9d0f-46be-aa61-c78498961274"
            className="rounded border bg-body"
            width={containerSize[0]}
            height={containerSize[1]}
          />
        ) : (
          <div
            ref={containerRef}
            className="bg-body rounded border h-100"
          >
            <FloatLoadingIndicator />
          </div>
        )}
      </Fragment>
    )
  }

  return null
}

const DashboardWrapper: FC = () => {
  const intl = useIntl()

  return (
    <>
      <PageTitle
        title={intl.formatMessage({ id: 'menu.dashboard' })}
        breadcrumbs={dashboardBreadCrumbs}
      />
      <DashboardPage />
    </>
  )
}

export { DashboardWrapper }
